import { Button, Dropdown, HelpDrawerToggle } from "@cmsgov/ds-medicare-gov";
import React, {
  ChangeEvent,
  FunctionComponent,
  useContext,
  useState,
} from "react";
import {
  AbstractBenefitPhaseEnum,
  AbstractBenefits,
  PharmacyPreferencesAndFrequencies,
} from "../../../@types";
import { AugmentedColumn } from "../../../@types/react-table";
import { RenderedTable } from "../../../components";
import FootnoteMarker from "../../../components/FootnoteMarker";
import Footnotes from "../../../components/Footnotes";
import Footnote from "../../../components/Footnote";
import {
  addRowAttributes,
  CONSTANTS,
  PreviewContext,
  sendTealiumEvent,
  useTableCaption,
  useTranslate,
} from "../../../helpers";
import {
  getTierNameList,
  getValidPhases,
  phaseHasFootnote,
  phaseTranslations,
  scanFootnotes,
  getCostGivenPhaseTierPref,
  filterOptions as getFilterOptions,
  dropdownTranslations,
  phaseHasGeneralCost,
  getGeneralCost,
  getCostsByDrugTierRowTestId,
  costsByDrugTierTableId,
} from "./costsByDrugTierHelpers";
import { LearnAboutTiers } from "./LearnAboutTiers";
import { CellValue, TableInstance } from "react-table";

export const noValueMarker = <span>&mdash;</span>;

export const CostsByDrugTierTable: FunctionComponent<{
  abstractBenefits: AbstractBenefits;
}> = ({ abstractBenefits }) => {
  const t = useTranslate();
  const hpmsPreview = useContext(PreviewContext);
  const [showDrugTierHelp, setShowDrugTierHelp] = useState(false);
  const columns: AugmentedColumn = [];
  const filterOptions = getFilterOptions(abstractBenefits);
  const [selectedFilter, setSelectedFilter] = useState<
    PharmacyPreferencesAndFrequencies
  >(filterOptions[0]);
  const [pharmPref, setPharmPref] = useState<PharmacyPreferencesAndFrequencies>(
    filterOptions[0]
  );
  const footnotes = scanFootnotes(abstractBenefits);
  const getTableCaption = useTableCaption({ genericElement: true });
  const caption = {
    title: t("plan_details.drug_coverage.cost_by_drug_tier.title"),
    render: function renderTableCaption(title: JSX.Element) {
      return (
        <>
          {title}
          <p>{t("plan_details.drug_coverage.cost_by_drug_tier.info")}</p>
          <HelpDrawerToggle
            helpDrawerOpen={showDrugTierHelp}
            showDrawer={(): void => {
              setShowDrugTierHelp(true);
              sendTealiumEvent("Help Drawer", "Learn About Tiers");
            }}
          >
            {t("plan_details.drug_coverage.cost_by_drug_tier.learn_more")}
          </HelpDrawerToggle>
          {showDrugTierHelp && (
            <LearnAboutTiers onSetShowDrugTierHelp={setShowDrugTierHelp} />
          )}
        </>
      );
    },
  };
  columns.push({
    accessor: "tier",
    Header: (
      <span className="mct-c-table__header-title ds-u-lg-display--block ds-u-display--none">
        {t("plan_details.cost_by_drug_tier.tiers")}
      </span>
    ),
    Footer: footnotes.length > 0 && (
      <Footnotes>
        {footnotes.map((footnote, fIdx) => (
          <Footnote
            key={footnote.phaseName}
            id={footnote.phaseName}
            className="ds-u-margin--0"
            marker={(fIdx + 1).toString()}
            text={t(footnote.footnote)}
          />
        ))}
      </Footnotes>
    ),
    Cell: ({ value, row }: Partial<TableInstance> | CellValue) => {
      if (typeof row?.original?.order !== "undefined") {
        addRowAttributes(row, {
          ["data-testid"]: getCostsByDrugTierRowTestId(row.original.order),
        });
      }
      return value;
    },
  });

  getValidPhases(abstractBenefits).map(phaseName => {
    const footnoteIndex = phaseHasFootnote(footnotes, phaseName)
      ? footnotes.findIndex(footnote => footnote.phaseName === phaseName) + 1
      : -1;

    columns.push({
      accessor: phaseName,
      Header: (
        <>
          <span className="mct-c-table__header-title">
            {t(phaseTranslations[phaseName])}
          </span>{" "}
          {footnoteIndex !== -1 && (
            <FootnoteMarker id={phaseName} marker={footnoteIndex.toString()} />
          )}
        </>
      ),
      Cell: function cellRenderer({
        value,
        row,
        column,
      }: Partial<TableInstance> | CellValue) {
        return <div data-testid={`${column.id}_${row.index}`}>{value}</div>;
      },
    });
  });

  const getRows = (pharmacyPreference: PharmacyPreferencesAndFrequencies) => {
    const rows:
      | Record<string, unknown>
      | { tier: string; order: number }[] = [];

    getTierNameList(abstractBenefits).forEach((tierName, tierIndex) => {
      const tier = tierName
        ? t(CONSTANTS.abstractBenefitTierTranslations[tierName])
        : `${t("plan_terms.tier")} ${tierIndex + 1}`;

      const row = {
        tier,
        order: tierIndex,
      };

      getValidPhases(abstractBenefits).forEach(phaseName => {
        const phase = abstractBenefits[phaseName];

        const showGeneralCost = phaseHasGeneralCost(
          abstractBenefits,
          phaseName as AbstractBenefitPhaseEnum
        );

        let cost: string | JSX.Element | undefined;

        if (showGeneralCost) {
          const generalCost = getGeneralCost(phase);

          cost = (
            <>
              <div>
                {t(
                  CONSTANTS.abstractBenefitTierTranslations
                    .COST_SHARE_TIER_GENERIC_DRUGS
                )}
                :
              </div>
              <div
                className="ds-u-margin-bottom--2"
                dangerouslySetInnerHTML={{
                  __html: generalCost.generic,
                }}
              />
              <div>
                {t(
                  CONSTANTS.abstractBenefitTierTranslations
                    .COST_SHARE_TIER_BRAND_NAME_DRUGS
                )}
                :
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: generalCost.brand,
                }}
              />
            </>
          );
        } else {
          cost = getCostGivenPhaseTierPref(phase, tierName, pharmacyPreference);
        }

        row[phaseName] = cost || noValueMarker;
      });
      rows.push(row);
    });
    return rows;
  };

  return (
    <>
      {getTableCaption(caption)}

      {!hpmsPreview && (
        <>
          <div className="CostByTier__filterContainer">
            <Dropdown
              label={t("plan_details.cost_by_drug_tier.tier_drug_cost_for")}
              name="tier-drug-cost-dropdown"
              value={selectedFilter}
              options={filterOptions.map(option => {
                return {
                  label: t(dropdownTranslations[option]),
                  value: option,
                };
              })}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                const value = event.currentTarget
                  .value as PharmacyPreferencesAndFrequencies;
                setSelectedFilter(value);
              }}
            />
            <Button
              className="CostByTier__changeButton"
              onClick={() => {
                setPharmPref(selectedFilter);
              }}
              disabled={pharmPref === selectedFilter}
            >
              {t("plan_details.costs_by_coverage_phase.change_filter")}
            </Button>
          </div>
          <RenderedTable
            id={costsByDrugTierTableId}
            tableColumns={columns}
            tableData={getRows(pharmPref)}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
        </>
      )}

      {hpmsPreview &&
        filterOptions.map(option => (
          <RenderedTable
            id={`${costsByDrugTierTableId}-${option}`}
            caption={t(dropdownTranslations[option])}
            key={option}
            tableColumns={columns}
            tableData={getRows(option)}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
        ))}
    </>
  );
};
